'use client';

/**
 * ReferenceRaster — one scene canvas of the living demo.
 *
 * Renders, bottom → top, in the reference pixel space of the scene:
 *   1. the canonical PNG (`data-testid="sn-raster"`), never cropped or filtered;
 *   2. the neon layer wrapper (`data-testid="sn-neon"`) hosting `NeonLayer`;
 *   3. the invisible hit-map: one button per hotspot with hover halo, label chip
 *      (`data-label`, mirrors `aria-label`), spotlight ring (`data-spotlight`);
 *   4. the insets wrapper (`data-testid="sn-insets"`) hosting `SceneInsets`.
 *
 * Every canvas owns its `--sn-unit` (1 % of its rendered width) through a
 * ResizeObserver so type inside insets scales with the artwork, not the window.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type AnimationEvent,
  type CSSProperties,
  type MouseEvent,
  type PointerEvent,
} from 'react';
import NeonLayer from '../living/light/NeonLayer';
import SceneInsets from '../living/insets';
import { CANVAS_UNIT_VAR, type Persona } from '../living/types';
import { hotspotsFor, type Rect } from './hotspots';
import type { ReferenceSceneId, ReferenceSceneProps } from './types';
import styles from './ReferenceRaster.module.css';

export interface ReferenceRasterMeta {
  src: string;
  width: number;
  height: number;
  reference: string;
}

export const REFERENCE_RASTER_META: Record<ReferenceSceneId, ReferenceRasterMeta> = {
  classroom: { src: '/studyninja/reference/classroom.png', width: 1586, height: 992, reference: '09' },
  motivation: { src: '/studyninja/reference/motivation.png', width: 1672, height: 941, reference: '08' },
  'teacher-goal': { src: '/studyninja/reference/teacher-goal.png', width: 1672, height: 941, reference: '05' },
  'knowledge-map': { src: '/studyninja/reference/knowledge-map.png', width: 1672, height: 941, reference: '06' },
  'personal-route': { src: '/studyninja/reference/personal-route.png', width: 1672, height: 941, reference: '07' },
  'learning-cycle': { src: '/studyninja/reference/learning-cycle.png', width: 1672, height: 941, reference: '02' },
  agency: { src: '/studyninja/reference/agency.png', width: 1672, height: 941, reference: '04' },
  'teacher-workspace': { src: '/studyninja/reference/teacher-workspace.png', width: 1672, height: 941, reference: '10' },
  technology: { src: '/studyninja/reference/technology.png', width: 1672, height: 941, reference: '03' },
  finale: { src: '/studyninja/reference/finale.png', width: 1672, height: 941, reference: '01' },
};

export interface ReferenceRasterProps extends ReferenceSceneProps {
  persona: Persona;
  /** Hotspot id carrying the pulsing ring (tour beat or nudge). */
  spotlight: string | null;
  /** Neon layer phase: 'off' while the scene is entering, 'on' afterwards. */
  neonPhase: 'off' | 'on';
  /** Transition role: 'in' = entering canvas, 'out' = leaving canvas, undefined = settled. */
  role?: 'in' | 'out';
  /** First paint image: fetchpriority high. */
  priority?: boolean;
  /** Render the hit-map and insets (false for the outgoing canvas). */
  interactive?: boolean;
  /** Fine pointer: append the D key hint to the chip of the focused hotspot. */
  keyHint?: boolean;
  onImageLoad?: () => void;
  /** Fires once the entering image finished its enter animation. */
  onEnterEnd?: () => void;
}

function rectStyle(rect: Rect, meta: ReferenceRasterMeta): CSSProperties {
  return {
    left: `${rect[0] / meta.width * 100}%`,
    top: `${rect[1] / meta.height * 100}%`,
    width: `${rect[2] / meta.width * 100}%`,
    height: `${rect[3] / meta.height * 100}%`,
  };
}

function isCircle(rect: Rect): boolean {
  return Math.abs(rect[2] - rect[3]) <= 12 && rect[2] < 220;
}

const TAP_CHIP_MS = 1600;

export default function ReferenceRaster(props: ReferenceRasterProps) {
  const {
    persona,
    spotlight,
    neonPhase,
    role,
    priority = false,
    interactive = true,
    keyHint = false,
    onImageLoad,
    onEnterEnd,
    ...sceneProps
  } = props;
  const meta = REFERENCE_RASTER_META[sceneProps.state.sceneId];
  const hotspots = hotspotsFor(sceneProps);
  const canvasRef = useRef<HTMLDivElement>(null);
  const tapTimerRef = useRef<number>(0);
  const [tapped, setTapped] = useState<string | null>(null);
  const [spark, setSpark] = useState<{ tick: number; x: number; y: number } | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof ResizeObserver === 'undefined') return;
    const write = (width: number) => canvas.style.setProperty(CANVAS_UNIT_VAR, `${(width / 100).toFixed(3)}px`);
    write(canvas.getBoundingClientRect().width);
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) write(entry.contentRect.width);
    });
    observer.observe(canvas);
    return () => observer.disconnect();
  }, []);

  useEffect(() => () => window.clearTimeout(tapTimerRef.current), []);

  const onInteraction = sceneProps.onInteraction;
  const onHotspotClick = useCallback((event: MouseEvent<HTMLButtonElement>, label: string, activate: () => void) => {
    const canvas = canvasRef.current;
    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      // Keyboard activation reports clientX/Y = 0: fall back to the button centre.
      const target = event.currentTarget.getBoundingClientRect();
      const px = event.clientX > 0 ? event.clientX : target.left + target.width / 2;
      const py = event.clientY > 0 ? event.clientY : target.top + target.height / 2;
      const fx = rect.width > 0 ? Math.min(100, Math.max(0, ((px - rect.left) / rect.width) * 100)) : 50;
      const fy = rect.height > 0 ? Math.min(100, Math.max(0, ((py - rect.top) / rect.height) * 100)) : 50;
      canvas.style.setProperty('--sn-fx', `${fx.toFixed(2)}%`);
      canvas.style.setProperty('--sn-fy', `${fy.toFixed(2)}%`);
      setSpark((current) => ({ tick: (current?.tick ?? 0) + 1, x: fx, y: fy }));
    }
    onInteraction?.(label);
    activate();
  }, [onInteraction]);

  const onHotspotPointerUp = useCallback((event: PointerEvent<HTMLButtonElement>, id: string) => {
    if (event.pointerType !== 'touch') return;
    window.clearTimeout(tapTimerRef.current);
    setTapped(id);
    tapTimerRef.current = window.setTimeout(() => setTapped(null), TAP_CHIP_MS);
  }, []);

  const onImageAnimationEnd = useCallback((event: AnimationEvent<HTMLImageElement>) => {
    if (event.target !== event.currentTarget) return;
    if (role === 'in') onEnterEnd?.();
  }, [onEnterEnd, role]);

  const canvasStyle = {
    '--canvas-aspect': `${meta.width} / ${meta.height}`,
  } as CSSProperties;

  return (
    <div
      ref={canvasRef}
      className={styles.canvas}
      style={canvasStyle}
      data-reference={meta.reference}
      data-scene={sceneProps.state.sceneId}
      data-role={role}
    >
      {/* The user-supplied PNG is intentionally rendered without an optimizer,
          crop, filter or generative reconstruction: it is the visual source of truth. */}
      <img
        key={meta.src}
        className={styles.image}
        data-testid="sn-raster"
        src={meta.src}
        width={meta.width}
        height={meta.height}
        alt=""
        aria-hidden="true"
        draggable={false}
        decoding="async"
        fetchPriority={priority ? 'high' : 'auto'}
        onLoad={onImageLoad}
        onAnimationEnd={onImageAnimationEnd}
      />
      <div className={styles.neon} data-testid="sn-neon" data-phase={neonPhase} aria-hidden="true">
        <NeonLayer scene={sceneProps.state.sceneId} phase={neonPhase} />
      </div>
      {interactive && (
        <div className={styles.hitMap} role="group" aria-label={sceneProps.lang === 'ru' ? 'Интерактивные области экрана' : 'Interactive screen regions'}>
          {hotspots.map((hotspot) => {
            const label = sceneProps.lang === 'ru' ? hotspot.ru : hotspot.en ?? hotspot.ru;
            const spotlighted = spotlight === hotspot.id;
            const circle = isCircle(hotspot.rect);
            const centerX = (hotspot.rect[0] + hotspot.rect[2] / 2) / meta.width;
            const style = rectStyle(hotspot.rect, meta) as CSSProperties & Record<'--hs-r', string>;
            if (circle) style['--hs-r'] = '50%';
            return (
              <button
                key={hotspot.id}
                type="button"
                className={styles.hotspot}
                style={style}
                aria-label={label}
                data-hotspot={hotspot.id}
                data-label={label}
                data-spotlight={spotlighted ? 'true' : undefined}
                data-tapped={tapped === hotspot.id ? 'true' : undefined}
                data-edge={hotspot.rect[1] < meta.height * 0.09 ? 'top' : undefined}
                data-align={centerX < 0.18 ? 'start' : centerX > 0.82 ? 'end' : undefined}
                data-key={keyHint ? 'D' : undefined}
                onClick={(event) => onHotspotClick(event, label, hotspot.activate)}
                onPointerUp={(event) => onHotspotPointerUp(event, hotspot.id)}
              >
                {spotlighted && <i className={styles.pulse} aria-hidden="true" />}
              </button>
            );
          })}
          {spark && (
            <span
              key={spark.tick}
              className={styles.spark}
              aria-hidden="true"
              style={{ left: `${spark.x}%`, top: `${spark.y}%` }}
            />
          )}
        </div>
      )}
      {interactive && (
        <div className={styles.insets} data-testid="sn-insets">
          <SceneInsets {...sceneProps} persona={persona} spotlight={spotlight} />
        </div>
      )}
    </div>
  );
}
